import { StandardEvents } from '@shopify/events';

class CartCrossSell extends HTMLElement {
  connectedCallback() {
    // (Re)initialize on EVERY connect: the cart morph either reuses this node
    // (leaving a wiped grid) or replaces it with a clone carrying copied
    // attributes (including any `initialized` flag), which used to strand the
    // rail blank with just its label until a full page reload. Re-read data
    // (the first cart item may have changed) and repopulate whenever real
    // cards are missing.
    this.grid = this.querySelector('[data-cart-cross-sell-grid]');
    this.productId = this.dataset.productId;
    this.collectionHandle = this.dataset.collectionHandle || '';
    this.limit = parseInt(this.dataset.limit, 10) || 3;

    // Re-bind (remove first): a replaced node would otherwise leave a stale
    // listener on the old instance and none on the new one.
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);

    if (
      this.grid &&
      !this.grid.querySelector('.cart-cross-sell__card:not(.cart-cross-sell__card--skeleton)')
    ) {
      // Reserve the rail's real height immediately (skeleton cards) so the drawer doesn't
      // grow/reflow after the async recommendations request resolves - that late layout
      // shift was pushing the cart item row out of view in the scrollable drawer.
      this.renderSkeleton();
      this.loadRecommendations();
    }

    // The native quantity/remove flow (component-cart-items.js) re-renders the whole
    // cart-drawer-section from the server on every change and morphs the live DOM
    // against that response in "hydration" mode. That server render has no memory of
    // the cards this component injects client-side, so the morph patches the grid back
    // down to its empty pre-hydration state. Reacting to the same cart-lines-update
    // event the native code dispatches (for add, remove AND quantity-change)
    // re-populates it every time, independent of the element's own connect lifecycle.

    // Belt-and-suspenders: the event-based repair above races a *second*, later morph
    // pass on add-to-cart (observed in practice: the grid gets populated, then wiped
    // again right after #handleCartLinesUpdate's repair already ran). Watch the DOM
    // directly instead of trusting event timing alone - if the grid ever goes back to
    // empty after having been populated, repopulate it.
    this.#observer?.disconnect();
    this.#observer = new MutationObserver(() => this.#repairIfEmpty());
    this.#observer.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#observer?.disconnect();
  }

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {boolean} */
  #isRepairing = false;

  #repairIfEmpty = () => {
    if (this.#isRepairing) return;
    this.grid = this.querySelector('[data-cart-cross-sell-grid]');
    if (!this.grid) return;
    if (this.grid.querySelector('.cart-cross-sell__card:not(.cart-cross-sell__card--skeleton)')) return;
    // Already showing skeletons - a load is already in flight, let it finish.
    if (this.grid.querySelector('.cart-cross-sell__card--skeleton')) return;

    this.#isRepairing = true;
    this.renderSkeleton();
    this.loadRecommendations().finally(() => {
      this.#isRepairing = false;
    });
  };

  #handleCartLinesUpdate = (event) => {
    const repair = () => {
      if (!this.isConnected) return;
      // Re-query: the morph may have replaced the grid node itself even though this
      // custom element instance was reused as-is.
      this.grid = this.querySelector('[data-cart-cross-sell-grid]');
      if (!this.grid) return;
      // Skip if the rail already shows real cards (avoids flicker + refetch).
      if (this.grid.querySelector('.cart-cross-sell__card:not(.cart-cross-sell__card--skeleton)')) return;
      this.renderSkeleton();
      this.loadRecommendations();
    };
    // The native flow attaches the morph promise, but not every update does:
    // without promise the rail stayed blank with just its label after the
    // morph wiped the injected cards. Repair either way.
    if (event && event.promise && typeof event.promise.then === 'function') {
      event.promise.then(repair).catch(() => {
        /* cart update failed server-side; leave whatever's currently shown as-is */
      });
    } else {
      Promise.resolve().then(repair);
    }
  };

  renderSkeleton() {
    if (!this.grid) return;
    this.grid.innerHTML = '';
    for (let i = 0; i < this.limit; i += 1) {
      const placeholder = document.createElement('div');
      placeholder.className = 'cart-cross-sell__card cart-cross-sell__card--skeleton';
      this.grid.appendChild(placeholder);
    }
  }

  async loadRecommendations() {
    if (!this.productId || !this.grid) {
      this.remove();
      return;
    }

    try {
      let products = await this.fetchRecommendations('complementary');
      if (!products.length) {
        products = await this.fetchRecommendations('related');
      }
      if (!products.length && this.collectionHandle) {
        products = await this.fetchCollectionProducts();
      }
      if (!products.length) {
        this.remove();
        return;
      }
      this.renderCards(products.slice(0, this.limit));
    } catch (error) {
      console.warn('cart-cross-sell: failed to load recommendations', error);
      this.remove();
    }
  }

  notifyResize() {
    // Nudge any scroll/height-observing components in the drawer (scroll-hint, etc.)
    // to re-measure now that the rail's real content has replaced the skeleton.
    try {
      window.dispatchEvent(new Event('resize'));
    } catch (error) {
      /* no-op */
    }
  }

  async fetchRecommendations(intent) {
    try {
      const url = `/recommendations/products.json?product_id=${encodeURIComponent(this.productId)}&limit=${this.limit}&intent=${intent}`;
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) return [];
      const data = await response.json();
      return Array.isArray(data.products) ? data.products : [];
    } catch (error) {
      console.warn(`cart-cross-sell: ${intent} recommendations failed`, error);
      return [];
    }
  }

  async fetchCollectionProducts() {
    try {
      // Fallback for stores without enough order history for Shopify's recommendation
      // engine: just show other products from the same collection as the cart item.
      const url = `/collections/${encodeURIComponent(this.collectionHandle)}/products.json?limit=${this.limit + 1}`;
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) return [];
      const data = await response.json();
      const products = Array.isArray(data.products) ? data.products : [];
      return products.filter((product) => String(product.id) !== String(this.productId));
    } catch (error) {
      console.warn('cart-cross-sell: collection fallback failed', error);
      return [];
    }
  }

  renderCards(products) {
    this.grid.innerHTML = '';
    products.forEach((product) => {
      this.grid.appendChild(this.buildCard(product));
    });
    this.notifyResize();
  }

  buildCard(product) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const variant = variants.find((v) => v.available) || variants[0];
    const image = this.extractImage(product);

    const card = document.createElement('div');
    card.className = 'cart-cross-sell__card';

    const img = document.createElement('img');
    img.src = image;
    img.alt = product.title || '';
    img.loading = 'lazy';
    img.width = 132;
    img.height = 132;
    card.appendChild(img);

    const title = document.createElement('p');
    title.className = 'cart-cross-sell__title';
    title.textContent = product.title || '';
    card.appendChild(title);

    const priceEl = document.createElement('p');
    priceEl.className = 'cart-cross-sell__price';
    priceEl.textContent = this.formatPrice(this.extractPrice(product, variant));
    card.appendChild(priceEl);

    const addButton = document.createElement('button');
    addButton.type = 'button';
    addButton.className = 'cart-cross-sell__add';

    if (!variant || !variant.available) {
      addButton.textContent = 'Agotado';
      addButton.disabled = true;
    } else {
      addButton.textContent = '+ Agregar';
      addButton.addEventListener('click', () => this.addToCart(variant.id, addButton));
    }

    card.appendChild(addButton);

    return card;
  }

  extractImage(product) {
    if (Array.isArray(product.images) && product.images.length) {
      const first = product.images[0];
      if (typeof first === 'string') return first;
      if (first && typeof first === 'object') return first.src || first.url || '';
    }
    if (product.featured_image) {
      return typeof product.featured_image === 'string' ? product.featured_image : product.featured_image.src || product.featured_image.url || '';
    }
    if (product.image && typeof product.image === 'object') {
      return product.image.src || product.image.url || '';
    }
    return '';
  }

  extractPrice(product, variant) {
    // Confirmed against this store's live response: /recommendations/products.json and
    // /collections/*/products.json both return price scaled by 100, same as cart.total_price
    // in Liquid and /cart.js - so it needs the /100 back (an earlier version of this file
    // removed it on a wrong assumption and showed prices 100x too high).
    const raw = (variant && variant.price) || product.price;
    return (Number(raw) || 0) / 100;
  }

  formatPrice(amount) {
    try {
      return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        maximumFractionDigits: 0,
      }).format(amount);
    } catch (error) {
      return `$${Math.round(amount)}`;
    }
  }

  async addToCart(variantId, button) {
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Agregando...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });

      if (!response.ok) {
        throw new Error('add to cart request failed');
      }

      button.textContent = 'Agregado';
      await this.refreshCartDrawer();
    } catch (error) {
      console.warn('cart-cross-sell: add to cart failed', error);
      button.disabled = false;
      button.textContent = originalText;
    }
  }

  async refreshCartDrawer() {
    try {
      const url = `${window.location.pathname}?sections=cart-drawer-section`;
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;

      const data = await response.json();
      const html = data['cart-drawer-section'];
      if (!html) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const freshInner = doc.querySelector('[data-hydration-key="cart-drawer-inner"]');
      const currentInner = document.querySelector('[data-hydration-key="cart-drawer-inner"]');

      if (freshInner && currentInner) {
        currentInner.replaceWith(freshInner);
      }
    } catch (error) {
      console.warn('cart-cross-sell: failed to refresh cart drawer', error);
    }
  }
}

if (!customElements.get('cart-cross-sell')) {
  customElements.define('cart-cross-sell', CartCrossSell);
}
